import React, { useEffect, useRef, useMemo, useCallback } from "react";
import StoryVoteCard from "./story-vote-card";
import { Story, StoryVote } from "../../../../types";
import { config } from "../../../../config";
import { useAppSelector } from "../../../../redux/store";
import FormPage from "../../../common/formpage/formpage";
import { useFormValidation } from "../../../../hooks/useFormValidation";
import { useDisplayForm } from "../../../../hooks/useDisplayForm";
import { useTranslation } from "react-i18next";
import { selectPlayer } from "../../../../redux/selectors";

type Props = {
  vote: StoryVote;

  onBack: () => void;
  onVote: (voteId: number, storyId: number) => void;
  toggleVote: (storyID?: number) => void;
  currentVote?: number;
  loading?: boolean;
};
const StoryVoteComponent = ({
  vote,
  onBack,
  onVote,
  toggleVote,
  currentVote,
  loading,
}: Props) => {
  const gameState = useAppSelector((state) => state.game); // Keep for now as this uses specific game stories
  const currentPlayer = useAppSelector(selectPlayer);

  const { t } = useTranslation(["game", "common"]);

  const voteId = useRef(vote.id);

  const containerRef = useRef<HTMLDivElement>(null);

  function handleSubmit() {
    if (currentVote) {
      onVote(vote.id, currentVote);
    } else {
      alert("YOU NEED TO VOTE FOR A STORY!");
    }
  }

  useEffect(() => {
    if (containerRef.current && vote.id !== voteId.current) {
      voteId.current = vote.id;
      containerRef.current.scrollIntoView();
    }
  }, [vote]);

  function handleBack() {
    onBack();
  }

  function checked(id: number): boolean {
    return currentVote === id;
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!event.target.checked) {
      toggleVote(undefined);
    } else {
      toggleVote(parseInt(event.target.value));
    }
  }

  function id(storyId: number) {
    return vote.id + ":" + storyId;
  }

  const { isReady } = useFormValidation({
    validationRules: [{ validate: () => currentVote !== undefined }],
    dependencies: [currentVote],
  });

  function filterStory(story: Story) {
    // let submissions = story.submissions.sort((a, b) => a.index - b.index);

    switch (vote.award.meta.rule) {
      case 0:
        return (
          config.canReactToSelf ||
          story.captain.id !== currentPlayer?.id ||
          gameState.game?.players.length === 1
        );
      case 1:
        return (
          config.canReactToSelf ||
          story.captain.id !== currentPlayer?.id ||
          gameState.game?.players.length === 1
        );
      case 2:
        return (
          config.canReactToSelf ||
          story.captain.id !== currentPlayer?.id ||
          gameState.game?.players.length === 1
        );
      case 3:
        return (
          config.canReactToSelf ||
          story.submissions[1].player_id !== currentPlayer?.id ||
          gameState.game?.players.length === 1
        );
      case 4:
        return (
          config.canReactToSelf ||
          story.submissions[2].player_id !== currentPlayer?.id ||
          gameState.game?.players.length === 1
        );
    }
    return false;
  }

  const { formPageProps } = useDisplayForm({
    customSubmit: handleSubmit,
    titles: [t("storyVote.headers.default")],
    submitText: t("button.vote", { ns: "common" }),
    validation: { isReady },
    subHeaders: [
      t("storyVote.subheaders.default"),
      vote.award.meta.rule === 0
        ? t("storyVote.subheaders.constant")
        : vote.award.meta.rule === 2
          ? t("storyVote.subheaders.constantTitle")
          : t("storyVote.subheaders.random"),
    ],
    readyFlash: true,
    countdownThreshold: 120,
    onBack: handleBack,
  });

  return (
    <FormPage {...formPageProps}>
      <div ref={containerRef} className="flex-grow flex flex-col items-center p-4">
        <div className="bg-black text-white py-1 px-4 shadow-[2px_2px_var(--color-gray-shadow)] text-2xl text-center">
          🏆 {vote.award.meta.name} 🏆
        </div>
        <div className="mt-1 text-lg text-center">
          {vote.award.meta.subtitle}
        </div>
        <div className="mt-4 self-stretch flex-grow flex flex-col items-stretch">
          {gameState.game?.stories?.filter(filterStory).map((s) => (
            <div key={"row" + s.id} className="self-stretch mb-4 flex flex-row items-center">
              <label className="flex-grow mr-4 w-[80%]" htmlFor={id(s.id)}>
                <StoryVoteCard
                  hideAuthors={gameState.game?.settings?.hide_authors}
                  story={s}
                  onClick={() => {
                    if (checked(s.id)) {
                      toggleVote(undefined);
                    } else {
                      toggleVote(s.id);
                    }
                  }}
                  disabled={loading}
                />
              </label>
              <input
                className="cursor-pointer w-[20%] [zoom:3]"
                id={id(s.id)}
                name="story-vote"
                type="checkbox"
                disabled={loading}
                checked={checked(s.id)}
                onChange={handleChange}
                value={s.id}
              />
            </div>
          ))}
        </div>
      </div>
    </FormPage>
  );
};

export default StoryVoteComponent;
